/**
 * Pitches, courts and pools, with their markings drawn in the shader.
 *
 * Each surface mesh carries local coordinates in metres from its centre and a
 * type id (see `SURFACE` in `core/build/amenities.ts`), and the shader lays
 * out the regulation markings from those: a football pitch's penalty areas and
 * centre circle, a clay court's service boxes, a pool's lane lines. Nothing
 * here is a texture, so every line is sharp at any distance.
 */

import { Color, MeshStandardMaterial, type WebGLProgramParametersWithUniforms } from 'three';
import { BAY_DEPTH, BAY_WIDTH } from '../core/build/parking.js';
import { GLSL_LAMP_LIGHT, floraNight, lampLight } from './flora.js';
import { terrainWeather } from './terrainMaterial.js';

const GLSL_SURFACE = /* glsl */ `
float surfLine(float d, float width) {
  float aa = max(fwidth(d), 0.002);
  return 1.0 - smoothstep(width * 0.5 - aa, width * 0.5 + aa, abs(d));
}
// Outline of an axis-aligned rectangle with half-extents h, line width w.
float surfBox(vec2 p, vec2 h, float w) {
  vec2 d = abs(p) - h;
  float inside = step(max(d.x, d.y), w * 0.5);
  return max(surfLine(d.x, w) * step(abs(p.y), h.y + w * 0.5),
             surfLine(d.y, w) * step(abs(p.x), h.x + w * 0.5)) * inside;
}
float surfHash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

vec3 surfaceColour(int type, vec2 p, float back, inout float roughness) {
  vec3 white = vec3(0.85);
  if (type <= 2) {
    // Football: striped turf and the full set of markings, scaled with the pitch.
    float s = type == 0 ? 1.0 : type == 1 ? 0.62 : 0.4;
    vec2 h = vec2(52.5, 34.0) * s;
    vec3 turf = vec3(0.09, 0.36, 0.045) * (mod(floor(p.x / 5.5), 2.0) < 1.0 ? 0.9 : 1.04);
    float w = 0.12;
    float m = surfBox(p, h, w);
    m = max(m, surfLine(p.x, w) * step(abs(p.y), h.y));
    m = max(m, surfLine(length(p) - 9.15 * s, w));
    for (int end = -1; end <= 1; end += 2) {
      float e = float(end);
      vec2 q = vec2(p.x - e * (h.x - 8.25 * s), p.y);
      m = max(m, surfBox(q, vec2(8.25 * s, 20.16 * s), w));
      vec2 g = vec2(p.x - e * (h.x - 2.75 * s), p.y);
      m = max(m, surfBox(g, vec2(2.75 * s, 9.16 * s), w));
      m = max(m, 1.0 - smoothstep(0.1, 0.16, length(vec2(p.x - e * (h.x - 11.0 * s), p.y))));
    }
    m = max(m, 1.0 - smoothstep(0.1, 0.16, length(p)));
    return mix(turf, white, m);
  }
  if (type == 3) {
    // Tennis: clay court, darker run-off, white lines.
    vec2 h = vec2(11.885, 5.485);
    bool court = abs(p.x) < h.x && abs(p.y) < h.y;
    vec3 clay = vec3(0.52, 0.17, 0.06) * (court ? 1.0 : 0.86) * (0.95 + 0.08 * surfHash(floor(p * 3.0)));
    float w = 0.08;
    float m = surfBox(p, h, w);
    m = max(m, surfLine(abs(p.y) - 4.115, w) * step(abs(p.x), h.x));
    m = max(m, surfLine(abs(p.x) - 6.4, w) * step(abs(p.y), 4.115));
    m = max(m, surfLine(p.y, w) * step(abs(p.x), 6.4));
    return mix(clay, white, m);
  }
  if (type == 4) {
    // Basketball: blue hard court with keys and circles.
    vec2 h = vec2(14.0, 7.5);
    vec3 court = (abs(p.x) < h.x && abs(p.y) < h.y) ? vec3(0.06, 0.16, 0.35) : vec3(0.1, 0.22, 0.12);
    float w = 0.08;
    float m = surfBox(p, h, w);
    m = max(m, surfLine(p.x, w) * step(abs(p.y), h.y));
    m = max(m, surfLine(length(p) - 1.8, w));
    for (int end = -1; end <= 1; end += 2) {
      float e = float(end);
      m = max(m, surfBox(vec2(p.x - e * (h.x - 2.9), p.y), vec2(2.9, 2.45), w));
      vec2 b = vec2(p.x - e * (h.x - 1.575), p.y);
      m = max(m, surfLine(length(b) - 6.75, w) * step(0.0, -e * b.x));
    }
    return mix(court, white, m);
  }
  if (type == 7) {
    // Car park bays: asphalt, a divider between each pair of bays and a line
    // across their backs. p.x runs along the row from its first bay's edge,
    // p.y across it from the middle; the back is at back × depth / 2.
    vec3 asphalt = vec3(0.14, 0.15, 0.16) * (0.95 + 0.08 * surfHash(floor(p * 0.8)));
    float half_ = ${(BAY_DEPTH / 2).toFixed(2)};
    float along = (fract(p.x / ${BAY_WIDTH.toFixed(2)} + 0.5) - 0.5) * ${BAY_WIDTH.toFixed(2)};
    // Dividers stop short of the open front, as painted bays do.
    float front = p.y * -back;
    // Antialiased by how fast p.x changes, not the divider distance: that wraps round
    // mid-bay, and its jump there would draw a dotted ghost line.
    float aa = max(fwidth(p.x), 0.002);
    float divider = 1.0 - smoothstep(0.05 - aa, 0.05 + aa, abs(along));
    float m = divider * step(front, half_ - 0.5);
    m = max(m, surfLine(p.y * back - half_ + 0.06, 0.1));
    return mix(asphalt, vec3(0.8), m);
  }
  // Pools: turquoise water inside a pale stone coping, lanes in the big one.
  vec2 h = type == 5 ? vec2(14.5, 8.0) : vec2(4.5, 2.5);
  float coping = type == 5 ? 1.2 : 0.45;
  if (abs(p.x) > h.x - coping || abs(p.y) > h.y - coping) return vec3(0.72, 0.7, 0.64);
  roughness = 0.12;
  float ripple = sin(p.x * 3.1 + sin(p.y * 2.3) * 1.7) * sin(p.y * 2.7 + p.x * 0.6);
  vec3 water = vec3(0.07, 0.42, 0.58) * (0.95 + 0.1 * ripple);
  if (type == 5) water = mix(water, vec3(0.02, 0.12, 0.3), surfLine(mod(p.y + 1.25, 2.5) - 1.25, 0.2) * step(abs(p.x), h.x - 3.0));
  return water;
}
`;

export function createSurfaceMaterial(): MeshStandardMaterial {
  const material = new MeshStandardMaterial({
    color: new Color(0xffffff),
    roughness: 0.9,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });
  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uNight = floraNight;
    shader.uniforms.uRoadSnow = terrainWeather.roadSnow;
    shader.uniforms.uLampMap = lampLight.map;
    shader.uniforms.uLampOrigin = lampLight.origin;
    shader.uniforms.uLampInvExtent = lampLight.invExtent;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
attribute vec4 aSurface;
varying vec4 vSurface;
varying vec2 vSurfaceWorld;`)
      .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>
  vSurface = aSurface;
  vSurfaceWorld = (modelMatrix * vec4(transformed, 1.0)).xz;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform float uNight;
uniform float uRoadSnow;
varying vec4 vSurface;
varying vec2 vSurfaceWorld;
${GLSL_SURFACE}
${GLSL_LAMP_LIGHT}`)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float surfaceRoughness = roughness;
  int surfaceType = int(vSurface.z + 0.5);
  vec3 surfaceRgb = surfaceColour(surfaceType, vSurface.xy, vSurface.w, surfaceRoughness);
  // Parking bays take the roads' dusting of snow while it snows (see roadMaterial.ts).
  if (surfaceType == 7 && uRoadSnow > 0.005) {
    vec2 cell = vSurfaceWorld * 0.7, f = fract(cell), w = f * f * (3.0 - 2.0 * f);
    vec2 i0 = floor(cell);
    float n = mix(mix(surfHash(i0), surfHash(i0 + vec2(1.0, 0.0)), w.x), mix(surfHash(i0 + vec2(0.0, 1.0)), surfHash(i0 + vec2(1.0, 1.0)), w.x), w.y);
    float cover = smoothstep(1.0 - uRoadSnow, 1.15 - uRoadSnow, n * 0.75 + surfHash(floor(vSurfaceWorld * 2.9)) * 0.25);
    surfaceRgb = mix(surfaceRgb, vec3(0.8, 0.84, 0.9), cover);
    surfaceRoughness = mix(surfaceRoughness, 0.7, cover);
  }
  diffuseColor.rgb *= surfaceRgb;`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
  roughnessFactor = surfaceRoughness;`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  totalEmissiveRadiance += streetLampRadiance(vSurfaceWorld, diffuseColor.rgb, uNight, 0.0);`);
  };
  return material;
}
